import React, { type PropsWithChildren, useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Flex, xcss } from '@atlaskit/primitives';
import TextField from '@atlaskit/textfield/text-field';
import type { TextfieldProps as TextFieldProps } from '@atlaskit/textfield/types';

import { ColorTile, type ColorTileProps } from '../../../../common/ui/color-tile';

export type ColorInputProps = PropsWithChildren<
	Pick<ColorTileProps, 'builtInColorMap' | 'value'> & {
		analyticsSource?: string;
		id?: string;
		name?: string;
		onChange(color?: string): void;
	}
>;

const colorTileContainerStyles = xcss({
	alignItems: 'center',
	paddingLeft: 'space.100',
});

export const ColorInput = ({
	analyticsSource,
	builtInColorMap,
	children,
	id,
	name,
	onChange,
	value,
}: ColorInputProps): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	value = value.toLowerCase();

	const onTextFieldChange = useCallback<NonNullable<TextFieldProps['onChange']>>(
		(event) => {
			onChange(event.currentTarget.value.toLowerCase());
		},
		[onChange],
	);

	const onTextFieldFocus = useCallback<NonNullable<TextFieldProps['onChange']>>(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'CustomColor',
				actionSubjectId: 'customPalette',
				source: analyticsSource,
			},
		}).fire();
	}, [analyticsSource, createAnalyticsEvent]);

	return (
		<TextField
			id={id}
			name={name}
			elemAfterInput={children}
			elemBeforeInput={
				<Flex xcss={colorTileContainerStyles}>
					<ColorTile builtInColorMap={builtInColorMap} value={value} />
				</Flex>
			}
			onChange={onTextFieldChange}
			onFocus={onTextFieldFocus}
			value={value.toUpperCase()}
		/>
	);
};
